// Klient API panelu rodzica (/api/admin/*). PIN jest przekazywany w nagłówku `x-parent-pin` (zakodowany przez encodeURIComponent —
// nagłówki HTTP przyjmują tylko znaki Latin-1) i trzymany wyłącznie w pamięci tej instancji klienta; nigdzie się go nie zapisuje.
import type { Gender } from './gender.ts';
import { RECORDING_CONSENT_VERSION } from './recordingConsent.ts';
import type { ThemeId } from './look.ts';

export interface AdminDevice {
  id: string;
  name: string;
  createdAt: string;
  lastSeen: string | null;
  revoked: boolean;
  tokensToday: number;
}
export interface Overview {
  version: string;
  limits: { devices: number; dailyTokens: number };
  devices: AdminDevice[];
}
/** Profil dziecka w panelu rodzica: dane, podsumowanie postępu i czas ostatniego zapisu z urządzenia. */
export interface AdminProfile {
  id: string;
  name: string;
  avatar: string;
  theme: ThemeId;
  gender: Gender;
  /** Czy profil ma PIN dziecka (sam PIN jest znany tylko rodzicowi; serwer trzyma jego skrót). */
  hasPin: boolean;
  archived: boolean;
  createdAt: string;
  progress: { lessonsDone: number; words: number; stars: number; streak: number; lastLessonDate: string | null } | null;
  syncedAt: string | null;
  /** Zgoda rodzica na zapisywanie nagrań głosu dziecka do strojenia oceny (SPEC §23.39); domyślnie wyłączona. */
  recordVoice: boolean;
  /** Wersja tekstu zgody, pod którą włączono zapis nagrań (0 = zapis wyłączony). Niższa od RECORDING_CONSENT_VERSION = zgoda na starszy tekst (SPEC §23.40.9). */
  recordConsentV: number;
  /** Ile nagrań profilu leży na serwerze, ile zajmują bajtów i kiedy była ostatnia próba. */
  recordings: { count: number; bytes: number; last: string | null };
}
export interface AdminProfiles {
  limit: number;
  /** Czy serwer ma prywatny bucket na nagrania (wiązanie RECORDINGS); bez niego przełącznik zgody jest nieaktywny. */
  recordingsAvailable: boolean;
  /** Nagrania profili, których już nie ma w bazie (twarde usunięcie SQL-em, README): panel pozwala je skasować. Starszy serwer nie wysyła tego pola. */
  orphanRecordings?: Array<{ id: string; count: number; bytes: number }>;
  profiles: AdminProfile[];
}
export interface ProfileLook {
  avatar?: string;
  theme?: ThemeId;
  /** Płeć dziecka (formy gramatyczne w tekstach). */
  gender?: Gender;
  /** PIN dziecka: 4 cyfry = ustaw, '' = usuń, brak pola = bez zmian (przy zakładaniu: brak = profil bez PIN-u). */
  pin?: string;
}
export interface SavedProfile {
  profile: { id: string; name: string; avatar: string; theme: ThemeId; gender: Gender; hasPin: boolean };
}

export type AdminFailure =
  | { ok: false; kind: 'bad-pin'; status: number; message: string }
  | { ok: false; kind: 'blocked'; status: number; message: string }
  | { ok: false; kind: 'no-server'; status: number; message: string }
  | { ok: false; kind: 'network'; status: number; message: string }
  | { ok: false; kind: 'conflict' | 'server' | 'other'; status: number; message: string };
export type AdminResult<T> = { ok: true; data: T } | AdminFailure;

export interface AdminClient {
  overview(): Promise<AdminResult<Overview>>;
  setDeviceRevoked(id: string, revoked: boolean): Promise<AdminResult<{ id: string; revoked: boolean }>>;
  profiles(): Promise<AdminResult<AdminProfiles>>;
  createProfile(input: { name: string } & ProfileLook): Promise<AdminResult<SavedProfile>>;
  updateProfile(id: string, patch: { name?: string } & ProfileLook): Promise<AdminResult<SavedProfile>>;
  archiveProfile(id: string, archived: boolean): Promise<AdminResult<{ id: string; archived: boolean }>>;
  /** Zeruje postęp profilu na serwerze; urządzenia ze starą kopią ją porzucą. */
  resetProfile(id: string): Promise<AdminResult<{ id: string; reset: boolean }>>;
  /** Włącza/wyłącza zgodę na nagrywanie głosu profilu do strojenia oceny. Włączenie wymaga bucketa (`recordingsAvailable`). */
  setRecording(id: string, enabled: boolean): Promise<AdminResult<{ id: string; enabled: boolean }>>;
  /** Kasuje partię najstarszych nagrań profilu (pliki i wiersze); `remaining` > 0 = wywołaj jeszcze raz. */
  deleteRecordings(id: string): Promise<AdminResult<{ id: string; deleted: number; remaining: number }>>;
}

const CONFLICTS: Record<string, string> = {
  'device-limit': 'Serwer ma już maksymalną liczbę aktywnych urządzeń.',
  'name-taken': 'Profil o takim imieniu już istnieje. Wybierz inne (może z inicjałem nazwiska).',
  'profile-limit': 'Jest już maksymalna liczba profili. Zarchiwizuj któryś, żeby dodać nowy.',
  'consent-outdated': 'Tekst zgody zmienił się od chwili, gdy otwarto tę stronę. Zamknij i otwórz aplikację jeszcze raz (odśwież stronę), przeczytaj nowy tekst i włącz zapis jeszcze raz.',
  'profile-archived': 'Profil jest zarchiwizowany — najpierw go przywróć, potem włącz zapis nagrań.',
  'recordings-unavailable': 'Serwer nie ma jeszcze prywatnego miejsca na nagrania (bucket naative-nagrania nie jest podłączony), więc nic nie da się włączyć ani skasować.',
};

const PROBLEMS: Record<string, string> = {
  'bad-profile-pin': 'PIN dziecka to dokładnie 4 cyfry (albo zostaw puste, żeby profil nie miał PIN-u).',
};

export function createAdminClient(pin: string, fetchImpl: typeof fetch = (...args) => fetch(...args), base = './api/admin'): AdminClient {
  async function call<T>(path: string, method: string, body?: unknown): Promise<AdminResult<T>> {
    let res: Response;
    try {
      res = await fetchImpl(`${base}/${path}`, {
        method,
        cache: 'no-store',
        headers: { 'x-parent-pin': encodeURIComponent(pin), ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (e) {
      return { ok: false, kind: 'network', status: 0, message: 'Brak połączenia z serwerem. Sprawdź internet i spróbuj jeszcze raz.' + (e instanceof Error && e.message ? ` (${e.message})` : '') };
    }
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      data = null; // np. strona HTML z hostingu bez backendu
    }
    if (res.ok && data !== null) return { ok: true, data: data as T };
    const obj = (typeof data === 'object' && data !== null ? data : {}) as { error?: unknown; retryAfterSec?: unknown };
    const error = typeof obj.error === 'string' ? obj.error : '';
    if (data === null) {
      if (res.status >= 500) return { ok: false, kind: 'server', status: res.status, message: `Serwer zwrócił błąd (HTTP ${res.status}) bez opisu — sprawdź, czy wykonano \`npm run db:migrate\` i wdrożenie.` };
      return { ok: false, kind: 'no-server', status: res.status, message: 'Ten adres nie ma serwera panelu (panel działa po wdrożeniu na Cloudflare, nie w trybie lokalnym).' };
    }
    if (res.status === 401) return { ok: false, kind: 'bad-pin', status: 401, message: 'Zły PIN.' };
    if (res.status === 429) return { ok: false, kind: 'blocked', status: 429, message: `Za dużo błędnych prób. Spróbuj za ok. ${Math.max(1, Math.round((Number(obj.retryAfterSec) || 900) / 60))} min.` };
    if (res.status === 409) return { ok: false, kind: 'conflict', status: 409, message: CONFLICTS[error] ?? (error || 'Konflikt.') };
    if (res.status >= 500) return { ok: false, kind: 'server', status: res.status, message: error || `Błąd serwera (HTTP ${res.status}).` };
    return { ok: false, kind: 'other', status: res.status, message: PROBLEMS[error] ?? (error || `HTTP ${res.status}`) };
  }
  return {
    overview: () => call<Overview>('overview', 'GET'),
    setDeviceRevoked: (id, revoked) => call('device', 'POST', { id, revoked }),
    profiles: () => call<AdminProfiles>('profiles', 'GET'),
    createProfile: (input) => call<SavedProfile>('profiles', 'POST', { action: 'create', ...input }),
    updateProfile: (id, patch) => call<SavedProfile>('profiles', 'POST', { action: 'update', id, ...patch }),
    archiveProfile: (id, archived) => call('profiles', 'POST', { action: 'archive', id, archived }),
    resetProfile: (id) => call('profiles', 'POST', { action: 'reset', id }),
    // włączając zgodę, wysyłamy numer wersji tekstu, który ta strona pokazała rodzicowi (serwer odrzuci zgodę na inny tekst, SPEC §23.40.9)
    setRecording: (id, enabled) => call('profiles', 'POST', { action: 'recording', id, enabled, ...(enabled ? { consentV: RECORDING_CONSENT_VERSION } : {}) }),
    deleteRecordings: (id) => call('profiles', 'POST', { action: 'recordings-delete', id }),
  };
}
